<template>
  <div>
    <NuxtLayout name="default" hero-section-id="admission-hero">
      <!-- hero section -->
      <section class="bg-light-1 pt-lg-space-xxl pb-lg-space-lg py-space-lg">
        <div class="container">
          <div class="row">
            <div class="col-12 col-lg-8 offset-lg-2">
              <div class="d-flex justify-content-center flex-column align-items-center">
                <div class="fw-bold mb-space-xs">
                  <span class="fs-medium mb-0">{{ $page?.whatItCosts?.title }}</span>
                </div>
                <h1 class="h1-large mb-space-xs text-center">
                  <span class="d-block">
                    {{ $page?.whatItCosts?.header }}
                  </span>
                  <span class="d-inline-block bg-secondary px-space-xxs">
                    {{ $page?.whatItCosts?.headerHighlighted }}
                  </span>
                </h1>
                <div
                  class="what-it-costs-hero__description mb-0 text-dark-3 text-center"
                  v-html="$page?.whatItCosts?.headerDescription"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- cost calculator -->
      <CostCalculatorV2
        :page-data="$page?.whatItCosts as unknown as Partial<WhatItCosts>"
        :programs="($page?.allDegreeSeekingProgram ?? []) as unknown as DegreeSeekingProgram[]"
        :transfer-credit-exception-program-codes="[]"
        @faq-link="openFaq"
      />

      <!-- take the next step -->
      <TakeNextStep
        :description="$page?.whatItCosts?.nextStepDescription!"
        :image="$page?.whatItCosts?.nextStepImage!"
        :image-alt="$page?.whatItCosts?.nextStepImageAlt!"
        @cta-click="handleTakeNextStepCtaClick"
      />

      <!-- turning education into opportunity component-->
      <section
        class="education-opportunity pb-space-lg pb-lg-space-xxl"
        aria-labelledby="education-opportunity-title"
      >
        <div class="container education-opportunity__container text-white">
          <div class="education-opportunity__media">
            <img
              :src="$page?.whatItCosts?.opportunityImage!"
              :alt="$page?.whatItCosts?.opportunityImageAlt!"
              class="education-opportunity__image"
            />
          </div>
          <div class="education-opportunity__content">
            <h2 id="education-opportunity-title" class="mb-space-sm h1-large" tabindex="0">
              {{ $page?.whatItCosts?.opportunityTitle }}
            </h2>
            <div
              class="fs-medium"
              tabindex="0"
              v-html="$page?.whatItCosts?.opportunityDescription"
            ></div>
          </div>
        </div>
      </section>

      <section
        class="video bg-dark-3 py-space-md py-md-space-lg py-lg-space-xxl"
        aria-labelledby="video-section-title"
      >
        <div class="container">
          <div class="row gx-0">
            <div class="col-12">
              <VideoModalAtlas
                class="video"
                :video-source="`https://www.youtube.com/embed/${$page?.whatItCosts?.video?.youtubeId}`"
                :bg-image-source="$page?.whatItCosts?.video?.imageSource!"
                :display-title="false"
                :display-border="false"
                @modal-open="
                  handleVideoModal('open', $page?.whatItCosts?.video?.title!, 'play button')
                "
                @modal-close="
                  handleVideoModal('close', $page?.whatItCosts?.video?.title!, 'close cross')
                "
              >
                <h2 id="video-section-title" class="mb-space-sm h2-medium" tabindex="0">
                  {{ $page?.whatItCosts?.video?.title }}
                </h2>
                <div tabindex="0" v-html="$page?.whatItCosts?.video?.description!"></div>
              </VideoModalAtlas>
            </div>
          </div>
        </div>
      </section>
      <!-- cost collection faq -->
      <section
        id="cost-calculator-faq"
        class="pt-space-lg pb-space-md pt-md-space-lg pb-md-space-sm py-lg-space-xxl position-relative faq-bg"
        :style="{ '--faq-bg': `url(${$page?.whatItCosts?.faqBgImage})` }"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium pb-space-sm">
                {{ $page?.whatItCosts?.faqTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <div
                v-for="(item, i) in $page?.whatItCosts?.faqList"
                :id="`faq-item-${i}`"
                :key="i"
                class="mb-space-xs bg-light-1"
              >
                <CollapseItem
                  :collapse-id="`faq-collapse-${i}`"
                  :display-number="false"
                  :title="item?.title!"
                  title-size="large"
                  @collapse-shown="handleCollapseClickEvent('open', item?.title!)"
                  @collapse-hidden="handleCollapseClickEvent('close', item?.title!)"
                >
                  <div v-non-rds-link v-external-links v-html="item?.description"></div>
                </CollapseItem>
              </div>
            </div>
          </div>
        </div>
      </section>
      <!-- card-icon section -->
      <section
        id="cost-calculator-contact"
        class="bg-light-2 pt-space-lg pb-space-md pt-md-space-lg pb-md-space-sm py-lg-space-xxl position-relative"
      >
        <div class="container">
          <div class="row">
            <div class="col-12 col-md-9">
              <h2 class="h2-medium pb-space-sm">
                {{ $page?.whatItCosts?.connectTitle }}
              </h2>
              <div class="pb-space-md" v-html="$page?.whatItCosts?.connectDescription"></div>
            </div>
          </div>
          <div class="row">
            <div
              v-for="(item, index) in $page?.whatItCosts?.connectCard"
              :key="index"
              class="col-12 col-md-6 col-lg-3 mb-space-sm"
            >
              <CardIcon
                :icon-source="item?.icon!"
                :icon-alt="item?.title!"
                :title="item?.title!"
                :cta-as-button="true"
                cta-button-variant="dark-3"
                :cta-link="item?.link?.uri!"
                cta-text-weight="bold"
                cta-text-variant="white"
                cta-text-size="small"
                :cta-text="item?.link?.text!"
                :cta-link-target="item?.link?.target as any"
                :clickable-card="false"
                class="card-deck h-100"
                @cta-click="handleCardClick(item?.link?.text!, item?.link?.uri!)"
              >
                <div class="mb-space-sm" v-html="item?.text!"></div>
              </CardIcon>
            </div>
          </div>
        </div>
      </section>
      <!-- SectionRfiMultiStep -->
      <SectionRfiMultistepNew
        :bg-image="RfiBgImage"
        class="mb-space-lg mb-lg-space-xxl"
      ></SectionRfiMultistepNew>
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import RfiBgImage from "@/assets/img/new/home-page/RFI-Image-v2.jpg";
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import { CardIcon } from "@rds-vue-ui/card-icon";
import { CollapseItem } from "@rds-vue-ui/collapse-item";
import { VideoModalAtlas } from "@rds-vue-ui/video-modal-atlas";
import SectionRfiMultistepNew from "~/components/home-page/SectionRfiMultistepNew.vue";
import CostCalculatorV2 from "~/components/what-it-costs-page/CostCalculatorV2.vue";
import TakeNextStep from "~/components/what-it-costs-page/TakeNextStep.vue";
import type { DegreeSeekingProgram, WhatItCosts } from "~/server/resolvers/resolvers-types";
import type { WhatItCostsQuery } from "~/types/graphql-operations";
import type { FaqLinkTarget } from "~/types/tuition-calculator";

const siteUrl = useRuntimeConfig().public.siteUrl;
const schemaService = useSchemaService();
const UtilityService = useUtilityService();
/*
********************************************
Page quries
********************************************
*/
const $page = await usePageQuery<WhatItCostsQuery>(
  /* GraphQL */ `
    #graphql
    query WhatItCosts($whatItCostsId: ID!) {
      whatItCosts(id: $whatItCostsId) {
        id
        detailPage
        heroImage
        title
        header
        headerHighlighted
        headerDescription
        metaTitle
        metaDescription
        heroLinkText
        tcBackgroundImage
        residencyStatusTitle
        residencyStatusDescription
        residencyStatusDescriptionMobile
        creditsWarningMessage
        step2TitleText
        step2TitleHighlight
        transferCreditsTitle
        transferCredotsDescription
        transferWarningMessage
        grantsTitle
        grantsDescription
        schorlarshipsTitle
        schorlarshipDescription
        employerBenefitTitle
        employerBenefitDescription
        nextStepDescription
        nextStepImage
        nextStepImageAlt
        overrideTitle
        overrideDescription
        opportunityTitle
        opportunityDescription
        opportunityImage
        opportunityImageAlt
        tcDisclaimer
        tcFootnote
        nextStepDescription
        nextStepImage
        nextStepImageAlt
        opportunityTitle
        opportunityDescription
        opportunityImage
        opportunityImageAlt
        video {
          id
          youtubeId
          title
          date
          duration
          description
          uploadDate
          imageSource
        }
        faqTitle
        faqBgImage
        faqList {
          title
          description
        }
        connectTitle
        connectDescription
        connectCard {
          title
          icon
          text
          link {
            text
            uri
            target
          }
        }
      }
      allDegreeSeekingProgram {
        id
        code
        title
        alternateTitle
        detailPage
        category {
          title
        }
        interestAreas {
          title
        }
        costOverride
        costAcademicYear
        latestAcademicYear
        programCode
        totalCreditHours
        tuitionBanner
        displayTransferCredits
        transferCreditsMaxValue
        transferCreditsError
        transferCreditsMinValue
        transferCreditsNote
        resProgramFeeCode
        noresProgramFeeCode
        costCollection {
          amount
        }
      }
    }
  `,
  {
    whatItCostsId: "7ac75f3e-9fc9-4044-b4af-a84833e2c88f"
  },
  { key: "what-it-costs-index" }
);

/*
********************************************
Page meta tags
********************************************
*/
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    ldjson: ["innerHTML"]
  }
});

const canonicalURL = UtilityService.formatCanonicalUrl(
  `${siteUrl}${$page?.whatItCosts?.detailPage}`
);

useHead({
  title: $page?.whatItCosts?.metaTitle,
  meta: [
    {
      name: "Title",
      content: $page?.whatItCosts?.metaTitle
    },
    {
      name: "Description",
      content: $page?.whatItCosts?.metaDescription
    },
    ...UtilityService.generateSocialTags(
      $page?.whatItCosts?.metaTitle as string,
      $page?.whatItCosts?.metaDescription as string,
      $page?.whatItCosts?.heroImage as string,
      canonicalURL
    )
  ],
  link: [
    {
      rel: "canonical",
      href: canonicalURL
    }
  ],
  script: [
    schemaService.generateWhatItCostsPageSchema($page?.whatItCosts as unknown as WhatItCosts)
  ]
});

const handleVideoModal = (action: string, section: string, text: string) => {
  analyticsComposable.trackModalEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
};

const handleCollapseClickEvent = (action: string, text: string) => {
  analyticsComposable.trackCollapseEvent(
    "onclick",
    action.toLowerCase(),
    "click",
    "main content",
    "faq about tuition",
    text.toLowerCase()
  );
};

const faqTitlePatterns: Record<FaqLinkTarget, RegExp> = {
  transfer: /transfer credit/i,
  financialAid: /grants and scholarships|financial aid/i,
  residency: /out-of-state tuition/i,
  fees: /fees included/i
};

const FAQ_SCROLL_GAP = 16;
const FAQ_COLLAPSE_TRANSITION_TIMEOUT = 400;

const waitForFaqCollapse = (): Promise<void> =>
  new Promise((resolve) => {
    const section = document.getElementById("cost-calculator-faq");
    if (!section) {
      resolve();
      return;
    }

    const finish = (event?: TransitionEvent) => {
      if (event && event.propertyName !== "height") return;
      section.removeEventListener("transitionend", finish);
      window.clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(finish, FAQ_COLLAPSE_TRANSITION_TIMEOUT);

    section.addEventListener("transitionend", finish);
  });

const vExternalLinks = {
  mounted: (el: HTMLElement) => openExternalLinksInNewTab(el),
  updated: (el: HTMLElement) => openExternalLinksInNewTab(el)
};

const openFaq = async (target: FaqLinkTarget) => {
  const faqList = $page?.whatItCosts?.faqList ?? [];
  const index = faqList.findIndex((item) => faqTitlePatterns[target].test(item?.title ?? ""));

  const question = index < 0 ? null : document.getElementById(`faq-item-${index}`);
  const toggle = question?.querySelector<HTMLButtonElement>("button");
  if (toggle && toggle.getAttribute("aria-expanded") !== "true") {
    toggle.click();
    await waitForFaqCollapse();
  }

  const stickyHeader = document.querySelector<HTMLElement>(".base-header.sticky-top");
  const headerOffset = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
  scrollToTarget(index < 0 ? "cost-calculator-faq" : `faq-item-${index}`, {
    offset: headerOffset + FAQ_SCROLL_GAP
  });
  toggle?.focus({ preventScroll: true });
};

const applyNonRdsLinkClass = (el: HTMLElement): void => {
  el.querySelectorAll("a").forEach((anchor) => anchor.classList.add("non-rds-link"));
};

const vNonRdsLink = {
  mounted: applyNonRdsLinkClass,
  updated: applyNonRdsLinkClass
};

const handleCardClick = (text: string, uri?: string) => {
  const isInternal = uri
    ? !/^https?:\/\//i.test(uri) || /(^|\.)asu\.edu(\/|$|:)/i.test(uri)
    : text.toLowerCase() === "locations";
  analyticsComposable.trackLinkEvent(
    isInternal ? "internal link" : "external link",
    "main content",
    $page?.whatItCosts?.connectTitle?.toLowerCase() || "NA",
    text.toLowerCase(),
    "card - icon"
  );
};

const handleTakeNextStepCtaClick = (text: string) => {
  analyticsComposable.trackLinkEvent(
    "external link",
    "main content",
    "take the next step",
    text.toLowerCase()
  );
};
</script>

<style lang="scss" scoped>
:deep(.video) {
  .card-body {
    background-color: var(--rds-dark-3);
    color: #fafafa !important;
  }
}
:deep(.card-icon-height) {
  height: 62px;
}
.what-it-costs-hero__description :deep(p:last-child) {
  margin-bottom: 0;
}
.title-underline {
  width: 64px;
  height: 10px;
}
.faq-bg {
  background-image: var(--faq-bg);
  position: relative;
  background-size: auto;
  background-position: top;
}
.education-opportunity__container {
  position: relative;
  display: flex;
  min-height: 420px;
  overflow: hidden;
  padding-right: 0;
  padding-left: 0;
}
.education-opportunity__container::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(270deg, #000 0%, rgba(0, 0, 0, 0) 100%);
  pointer-events: none;
}
.education-opportunity__media {
  position: absolute;
  inset: 0;
}
.education-opportunity__image {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 420px;
  object-fit: cover;
  object-position: left center;
}
.education-opportunity__content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: var(--rds-space-md, 32px) var(--rds-space-xs, 16px);
}
@media (max-width: 767px) {
  .education-opportunity__container {
    width: calc(100% - 32px);
    min-height: 674px;
    background-color: #000;
  }
  .education-opportunity__container::after {
    display: none;
  }
  .education-opportunity__media {
    inset: 0 0 auto;
    width: 100%;
    height: min(465px, calc((100vw - 32px) * 465 / 361));
    max-height: none;
    aspect-ratio: auto;
    background-color: #000;
  }
  .education-opportunity__media::after {
    content: "";
    position: absolute;
    inset: 0 0 -2px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, #000 98%);
    pointer-events: none;
  }
  .education-opportunity__image {
    width: 100%;
    height: 100%;
    min-height: 0;
    object-fit: cover;
    object-position: 10% center;
  }
  .education-opportunity__content {
    justify-content: flex-end;
    padding: var(--rds-space-md, 32px);
  }
  .education-opportunity__content h1 {
    max-width: 260px;
  }
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .education-opportunity__container {
    min-height: 420px;
  }
  .education-opportunity__image {
    min-height: 420px;
  }
  .education-opportunity__content {
    width: 52%;
    margin-left: auto;
    padding: var(--rds-space-xl, 64px) var(--rds-space-md, 32px) var(--rds-space-xl, 64px);
  }
}

/* Large devices (desktops, 992px and up) */
@media (min-width: 992px) {
  .education-opportunity__container {
    min-height: 520px;
  }
  .education-opportunity__image {
    min-height: 520px;
  }
  .education-opportunity__content {
    width: 50%;
    padding-right: var(--rds-space-xl, 64px);
  }
  .education-opportunity__content h1 {
    font-size: 56px;
  }
}
</style>
